import { useEffect, useState } from "react";
import { t } from "@/i18n/locale";

/** Pseudo progress for a launch. It is an impression, not a measurement: the
 *  real phases stay in the launch hooks (and stay unlabelled in the UI).
 *
 *  - Monotonic while `active`; it never goes backwards.
 *  - Capped at 95% until the surface reports operable, then 100% and gone.
 *  - Layout-neutral: 3px, absolutely positioned by the surface header. */
const READY_CAP = 95;
const TICK_MS = 220;
const HIDE_AFTER_DONE_MS = 260;

export default function LaunchProgress({ active, complete, className = "" }: {
  active: boolean;
  complete: boolean;
  className?: string;
}) {
  const [value, setValue] = useState(0);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (complete) {
      setVisible(true);
      setValue(100);
      const hide = window.setTimeout(() => {
        setVisible(false);
        setValue(0);
      }, HIDE_AFTER_DONE_MS);
      return () => window.clearTimeout(hide);
    }
    if (active) {
      setVisible(true);
      setValue(previous => (previous > 0 && previous < 100 ? Math.min(previous, READY_CAP) : 4));
      const tick = window.setInterval(() => {
        setValue(previous => previous >= READY_CAP
          ? READY_CAP
          : Math.min(READY_CAP, previous + Math.max(0.35, (READY_CAP - previous) * 0.055)));
      }, TICK_MS);
      return () => window.clearInterval(tick);
    }
    setVisible(false);
    setValue(0);
  }, [active, complete]);
  return (
    <div
      className={`launch-progress ${className}`.trim()}
      data-visible={visible ? "true" : "false"}
      role={visible ? "progressbar" : undefined}
      aria-label={visible ? t("Loading app…") : undefined}
      aria-valuemin={visible ? 0 : undefined}
      aria-valuemax={visible ? 100 : undefined}
      aria-valuenow={visible ? Math.round(value) : undefined}
      aria-hidden={visible ? undefined : true}
    >
      <span className="launch-progress-fill" style={{ width: `${value}%` }} />
    </div>
  );
}
